import React, {useState, useEffect} from 'react';
import { useNavigate, useParams } from 'react-router';
import Select, {components} from 'react-select';
import { useCompanyUpdate, useCompanyPermissionUpdate } from './CompanyContext';
import { getCompanies, getPermissionsForCompany } from '../../service/CompanyService';
import { DropdownIndicator } from '../CustomDropdownIndicator/CustomDropdownIndicator'
import { formatTestId, getUrl } from '../../util/EfupUtility';

export default function CompanySelection() {

    const navigate = useNavigate();

    const [companyOptions, setCompanyOptions] = useState(null);

    const changeSelectedCompany = useCompanyUpdate();
    const changeCompanyPermission = useCompanyPermissionUpdate();
    const urlParams = useParams();
    const urlCompanyNumber = urlParams?.companyNumber;

    const dark = '#1F1F26';
    const darkAccent = '#3F3F46';
    const light = '#DDDFE2';

    // Manually specifying the option so we can add data-testid to it
    const Option = ({ children, ...props }) => {
        const selectType = props.selectProps.customProps.selectType;
        const testId = formatTestId(`${selectType}-${props.label}-option`)
    
        return (
            <components.Option {...props}>
                <div data-testid={testId}>
                    {children}
                </div>
            </components.Option>
        );
    };

    const customTheme = (theme) => ({
        ...theme,
        borderRadius: 0,
        colors: {
            ...theme.colors,
            primary: {dark}, // border,hover dropdown
            primary50: {darkAccent}, // on click
            primary25: {darkAccent}, // hover
            neutral0: {dark}, // default background
            neutral60: {light}, // dropdown-selected
            neutral80: {light}, // select text
            // colors not defined (primary75,danger,dangerLight,neutral5,neutral10,neutral30,neutral40,neutral70,neutral90)
        }
    })

    const customStyles = {
        control: (provided) => ({
            ...provided,
            maxWidth: '250px', // constrain width
        }),
        indicatorSeparator: () => ({
            display:'none' // hide separator
        }),
        menu: (provided) => ({
            ...provided, 
            border: '1px solid #DDDFE2', // border around dropdown
            backgroundColor:'#2F2F36', // without this as a literal the dropdown is transparent
        }),
        option: (styles, { isDisabled, isFocused, isSelected }) => {
            return {
                ...styles,
                backgroundColor: isDisabled
                    ? undefined
                    : isSelected && !isFocused
                        ? 'transparent'
                        : isFocused
                            ? '#000'
                            : undefined,
                cursor: isDisabled ? 'not-allowed' : 'default',

                ':active': {
                    ...styles[':active'],
                    backgroundColor: !isDisabled
                        ? isSelected
                            ? '#000'
                            : '#000'
                        : undefined,
                },
                ':hover': {
                    backgroundColor: !isDisabled
                        ? isSelected
                            ? '#000'
                            : '#000'
                        : undefined,
                }
            };
        },
    }

    function changeCompany(company) {
        const {companyNumber} = company;

        // update company
        changeSelectedCompany(company)

        //clear out any preexisting permissions when company changes
        changeCompanyPermission(null);

        // get permissions for company
        getPermissionsForCompany(companyNumber, (permissions) => { 
            changeCompanyPermission(permissions);
        } );

        // navigate to company dashboard if:
        //   redirected from base URL OR
        //   the whole page is being reloaded
        if (urlCompanyNumber === "default" || companyNumber !== urlCompanyNumber) {
            // clear out all preexisting cached values
            window.sessionStorage.clear();
            
            // move to dashboard
            navigate(
                getUrl(companyNumber, '/dashboard'),
                {
                    state: {
                        companyNumber: companyNumber
                    }
                }
            )
        }
    }

    // process results of getting list of companies
    function readOptions(companyList) {

        setCompanyOptions(companyList);

        let selectedCompany = findCompany(companyList, urlCompanyNumber);
        changeCompany(selectedCompany);
    }

    // find company with companyNumber, default to first company if not found
    function findCompany(companyList, companyNumber) {
        if (!companyList) {
            return;
        }

        let company = companyList.find(company => {return company.companyNumber === companyNumber});
        return company ? company : companyList[0];
    }

    useEffect(() => {
        getCompanies(readOptions);
    }, []);     // eslint-disable-line react-hooks/exhaustive-deps

    let selectedCompany = findCompany(companyOptions, urlCompanyNumber);

    return (
        <div className="co-select">

            {companyOptions != null && companyOptions.length > 1 &&
            <Select
                defaultValue={selectedCompany}
                onChange={changeCompany}
                placeholder="Select a company"
                components={{DropdownIndicator, Option}}
                customProps={{ selectType: 'company' }}
                options={companyOptions}
                styles={customStyles}
                theme={customTheme}
                noOptionsMessage={()=>'No companies'}
            /> }
        </div>
    );
}
